import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FadeInSection } from '../components/FadeInSection';

interface BookFormData {
  fullName: string;
  phone: string;
  reason: string;
  preferredDate: string;
  preferredTime: string;
  patientType: 'new' | 'existing' | '';
  medicalAid: string;
  notes: string;
  popiaConsent: boolean;
}

interface FormErrors {
  fullName?: string;
  phone?: string;
  reason?: string;
  preferredDate?: string;
  preferredTime?: string;
  patientType?: string;
  popiaConsent?: string;
}

function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

export function BookPage() {
  useEffect(() => {
    document.title = 'Book an Appointment | IKhanGynae';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Request an appointment with Dr Imraan Khan, OB/GYN in Cape Town Southern Suburbs. Fill in the form and we will confirm as soon as possible.'
      );
    }
  }, []);

  const emptyForm: BookFormData = {
    fullName: '',
    phone: '',
    reason: '',
    preferredDate: '',
    preferredTime: '',
    patientType: '',
    medicalAid: '',
    notes: '',
    popiaConsent: false,
  };

  const [form, setForm] = useState<BookFormData>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function validate(): FormErrors {
    const e: FormErrors = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required.';
    if (!form.phone.trim()) e.phone = 'Phone number is required.';
    if (!form.reason.trim()) e.reason = 'Reason for visit is required.';
    if (!form.preferredDate) e.preferredDate = 'Preferred date is required.';
    if (!form.preferredTime) e.preferredTime = 'Preferred time is required.';
    if (!form.patientType) e.patientType = 'Please select new or existing patient.';
    if (!form.popiaConsent) e.popiaConsent = 'You must consent to proceed.';
    return e;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      await fetch('/api/appointment-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch {
      // Silently continue — backend may not be configured yet
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 py-16"
        style={{ backgroundColor: 'var(--color-cream)' }}
      >
        <FadeInSection className="bg-white rounded-2xl shadow-md p-10 max-w-lg w-full text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2
            className="font-heading text-2xl mb-3"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 400 }}
          >
            Request Received!
          </h2>
          <p className="text-sm font-body mb-2" style={{ color: 'var(--color-navy)', opacity: 0.7, fontFamily: 'var(--font-body)' }}>
            Thank you, <strong>{form.fullName}</strong>. We'll confirm your appointment via WhatsApp as soon as possible.
          </p>
          <p className="text-xs font-body mb-6" style={{ color: 'var(--color-navy)', opacity: 0.5, fontFamily: 'var(--font-body)' }}>
            You'll receive a WhatsApp message from our practice number confirming your booking.
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm(emptyForm); }}
            className="text-sm font-body font-medium underline"
            style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
          >
            Book another appointment
          </button>
          <div className="mt-4">
            <Link to="/" className="text-xs font-body opacity-50 hover:underline" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-navy)' }}>
              ← Back to home
            </Link>
          </div>
        </FadeInSection>
      </div>
    );
  }

  const inputClass = (field: keyof FormErrors) =>
    `w-full px-3 py-2.5 border rounded-lg text-sm font-body focus:outline-none focus:ring-2 transition-colors bg-white ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'
    }`;

  return (
    <div>
      {/* ── Hero Banner ── */}
      <section
        className="py-20 px-4 text-white text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <p
            className="font-heading italic mb-2"
            style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
          >
            IKhanGynae, Cape Town Southern Suburbs
          </p>
          <h1
            className="font-heading"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
              color: '#fff',
            }}
          >
            Book an Appointment
          </h1>
          <div
            className="mx-auto mt-4"
            style={{ width: 60, height: 2, backgroundColor: 'var(--color-gold)', opacity: 0.7 }}
          />
          <p className="text-white/70 mt-4 font-body text-sm max-w-md mx-auto" style={{ fontFamily: 'var(--font-body)' }}>
            Fill in the form below and we'll confirm your appointment as soon as possible.
          </p>
        </div>
      </section>

      {/* ── Form ── */}
      <section className="py-16 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-2xl mx-auto">
          <FadeInSection>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 space-y-5" noValidate>

              {/* Full Name */}
              <div>
                <label htmlFor="b-name" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="b-name"
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className={inputClass('fullName')}
                  style={{ fontFamily: 'var(--font-body)' }}
                  placeholder="Your full name"
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="b-phone" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="b-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputClass('phone')}
                  style={{ fontFamily: 'var(--font-body)' }}
                  placeholder="+27 xx xxx xxxx"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Reason */}
              <div>
                <label htmlFor="b-reason" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Reason for Visit <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="b-reason"
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className={`${inputClass('reason')} resize-none`}
                  style={{ fontFamily: 'var(--font-body)' }}
                  placeholder="Briefly describe the reason for your visit..."
                />
                {errors.reason && <p className="text-red-500 text-xs mt-1">{errors.reason}</p>}
              </div>

              {/* Date + Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="b-date" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    Preferred Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="b-date"
                    type="date"
                    min={getToday()}
                    value={form.preferredDate}
                    onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                    className={inputClass('preferredDate')}
                    style={{ fontFamily: 'var(--font-body)' }}
                  />
                  {errors.preferredDate && <p className="text-red-500 text-xs mt-1">{errors.preferredDate}</p>}
                </div>
                <div>
                  <label htmlFor="b-time" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    Preferred Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="b-time"
                    type="time"
                    value={form.preferredTime}
                    onChange={(e) => setForm({ ...form, preferredTime: e.target.value })}
                    className={inputClass('preferredTime')}
                    style={{ fontFamily: 'var(--font-body)' }}
                  />
                  <p className="text-xs mt-1 font-body opacity-50" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>Suggested: 08:00 – 17:00</p>
                  {errors.preferredTime && <p className="text-red-500 text-xs mt-1">{errors.preferredTime}</p>}
                </div>
              </div>

              {/* Patient type */}
              <div>
                <p className="text-xs font-body font-medium mb-2" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Are you a new or existing patient? <span className="text-red-500">*</span>
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="patientType"
                      value="new"
                      checked={form.patientType === 'new'}
                      onChange={() => setForm({ ...form, patientType: 'new' })}
                      className="accent-teal-700"
                    />
                    <span className="text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>New patient</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="patientType"
                      value="existing"
                      checked={form.patientType === 'existing'}
                      onChange={() => setForm({ ...form, patientType: 'existing' })}
                      className="accent-teal-700"
                    />
                    <span className="text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>Existing patient</span>
                  </label>
                </div>
                {errors.patientType && <p className="text-red-500 text-xs mt-1">{errors.patientType}</p>}
              </div>

              {/* Medical aid (optional) */}
              <div>
                <label htmlFor="b-aid" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Medical Aid Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="b-aid"
                  type="text"
                  value={form.medicalAid}
                  onChange={(e) => setForm({ ...form, medicalAid: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 bg-white"
                  style={{ fontFamily: 'var(--font-body)' }}
                  placeholder="e.g. Discovery, Bonitas, Medihelp"
                />
              </div>

              {/* Notes (optional) */}
              <div>
                <label htmlFor="b-notes" className="block text-xs font-body font-medium mb-1.5" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Additional Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="b-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-body focus:outline-none focus:ring-2 resize-none bg-white"
                  style={{ fontFamily: 'var(--font-body)' }}
                  placeholder="Anything else you'd like us to know..."
                />
              </div>

              {/* POPIA Consent */}
              <div
                className={`p-4 rounded-lg border ${errors.popiaConsent ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
                style={{ backgroundColor: errors.popiaConsent ? undefined : 'var(--color-cream)' }}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.popiaConsent}
                    onChange={(e) => setForm({ ...form, popiaConsent: e.target.checked })}
                    className="mt-1 flex-shrink-0 accent-teal-700"
                  />
                  <span className="text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    I consent to my personal information being processed by IKhanGynae for the purpose of scheduling this appointment, in accordance with POPIA.
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </label>
                {errors.popiaConsent && <p className="text-red-500 text-xs mt-1 ml-7">{errors.popiaConsent}</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-lg font-body font-semibold text-sm text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
              >
                {submitting ? 'Submitting...' : 'Submit Appointment Request'}
              </button>
            </form>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}
